import { useGetCrmProjectsByCrmId } from "@/features/crm_project/hooks/useQuery/useGetCrmProjectsByCrmId";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { findCloseMatchAndSort } from "@/lib/utils/stringMatcher/findCloseMatchAndSort";
import { useCallback, useState } from "react";
import { CrmConfigImportFileObject, CrmProjectImportFileObject } from "../../components/organisms/ImportConfigsDialog/types";
import { deduplicateObjectArray } from "@/lib/utils/object";

export interface ValidatedCrmConfig extends CrmConfigImportFileObject {
    configExists: boolean;
    closeMatches?: string[];
    duplicates: number;
}

export interface ValidatedCrmProject extends Omit<CrmProjectImportFileObject, 'crm_config'> {
    crmProjectExists: boolean;
    closeMatches?: string[];
    crm_config: ValidatedCrmConfig[];
    duplicates: number;
}

const CLOSE_MATCH_THRESHOLD = 0.5;
const MAX_CLOSE_MATCHES = 3;

function findCloseMatches(name: string, existingNames: string[]): string[] | undefined {
    const matches = findCloseMatchAndSort(name, existingNames)
        .filter(match => match.rating > CLOSE_MATCH_THRESHOLD)
        .slice(0, MAX_CLOSE_MATCHES)
        .map(match => match.member);

    return matches.length > 0 ? matches : undefined;
}



function validateConfigs(
    configsToImport: CrmConfigImportFileObject[],
    existingConfigs: CrmConfigImportFileObject[]
): ValidatedCrmConfig[] {
    const existingConfigNames = existingConfigs.map(c => c.name);
    const deduplicatedConfigs = deduplicateObjectArray(configsToImport, 'name');
    return deduplicatedConfigs.map(({ item: config, duplicates }) => {
        const existingConfig = existingConfigs.find(c => c.name === config.name);

        if (existingConfig) {
            return {
                ...config,
                duplicates,
                configExists: true,
                closeMatches: undefined
            };
        } else {
            return {
                ...config,
                duplicates,
                configExists: false,
                closeMatches: findCloseMatches(config.name, existingConfigNames)
            };
        }
    });
}



export function useValidateCrmProjects(crmId: Tables<'crm'>['id'] | undefined) {
    const [validatedCrmProjects, setValidatedCrmProjects] = useState<ValidatedCrmProject[]>([]);

    const {
        data: existingCrmProjects,
        error: existingCrmProjectsError,
        isLoading: isLoadingExistingCrmProjects
    } = useGetCrmProjectsByCrmId(crmId);

    const validate = useCallback((crmProjects: CrmProjectImportFileObject[]) => {
        if (!crmProjects) return;

        const existingProjectNames = existingCrmProjects?.map(p => p.name) ?? [];
        const deduplicatedProjects = deduplicateObjectArray(crmProjects, 'name');

        const validated = deduplicatedProjects.map(({ item: crmProject, duplicates: projectDuplicates }) => {
            const existingProject = existingCrmProjects?.find(p => p.name === crmProject.name);

            if (existingProject) {
                const validatedConfigs = validateConfigs(crmProject.crm_config, existingProject.crm_config.map(c => ({
                    ...c,
                    id: String(c.id)
                }
                )));

                return {
                    ...crmProject,
                    duplicates: projectDuplicates,
                    crmProjectExists: true,
                    closeMatches: undefined,
                    crm_config: validatedConfigs
                };
            } else {
                const validatedConfigs = deduplicateObjectArray(crmProject.crm_config, 'name').map(({ item: config, duplicates }) => ({
                    ...config,
                    duplicates,
                    configExists: false
                }));

                return {
                    ...crmProject,
                    duplicates: projectDuplicates,
                    crmProjectExists: false,
                    closeMatches: findCloseMatches(crmProject.name, existingProjectNames),
                    crm_config: validatedConfigs
                };
            }
        }).sort((a, b) => {
            // First: projects that exist
            if (a.crmProjectExists && !b.crmProjectExists) return -1;
            if (!a.crmProjectExists && b.crmProjectExists) return 1;

            // Both don't exist - sort by close matches
            if (!a.crmProjectExists && !b.crmProjectExists) {
                const aHasMatches = !!a?.closeMatches && a?.closeMatches.length > 0;
                const bHasMatches = !!b?.closeMatches && b?.closeMatches.length > 0;

                if (aHasMatches && !bHasMatches) return -1;
                if (!aHasMatches && bHasMatches) return 1;
            }

            return 0;
        });

        setValidatedCrmProjects(validated);
    }, [existingCrmProjects]);

    return {
        validatedProjects: validatedCrmProjects,
        validateProjects: validate,
        isLoading: isLoadingExistingCrmProjects,
        error: existingCrmProjectsError
    };
}